import { useEffect, useRef, useMemo } from 'react';
import './AboutPanel.css';

interface AboutPanelProps {
  isOpen: boolean;
  onClose: () => void;
}

const CHEATSHEET = [
  {
    label: 'Variable Declaration',
    code: `global pi is 3.14
local radius is 5`,
  },
  {
    label: 'Variable Mutation',
    code: `local count is 0
count is now count + 1`,
  },
  {
    label: 'I/O',
    code: `tell {Hello, World!}
ask name`,
  },
  {
    label: 'Arithmetic',
    code: `local area is pi * radius ^ 2
local avg is (a + b) / 2`,
  },
  {
    label: 'String Concatenation',
    code: `tell {Hello, } name {. You are } age { years old.}`,
  },
  {
    label: 'Comments',
    code: `< single-line comment >
<< multi-line
   comment >>`,
  },
];

interface Token {
  text: string;
  type: 'keyword' | 'number' | 'string' | 'comment' | 'operator' | 'text';
}

function tokenizeZeta(code: string): Token[] {
  const tokens: Token[] = [];
  const lines = code.split('\n');
  let inBlockComment = false;

  for (let li = 0; li < lines.length; li++) {
    const line = lines[li];
    let pos = 0;
    while (pos < line.length) {
      const remaining = line.slice(pos);

      // Continuation of a << >> block comment from a previous line
      if (inBlockComment) {
        const end = remaining.indexOf('>>');
        if (end !== -1) {
          tokens.push({ text: remaining.slice(0, end + 2), type: 'comment' });
          pos += end + 2;
          inBlockComment = false;
          continue;
        }
        tokens.push({ text: remaining, type: 'comment' });
        break;
      }

      // Block comment << ... >>
      if (remaining.startsWith('<<')) {
        const end = remaining.indexOf('>>');
        if (end !== -1) {
          tokens.push({ text: remaining.slice(0, end + 2), type: 'comment' });
          pos += end + 2;
          continue;
        }
        tokens.push({ text: remaining, type: 'comment' });
        inBlockComment = true;
        break;
      }

      // Single-line comment < ... >
      if (remaining.startsWith('<')) {
        const end = remaining.indexOf('>');
        if (end !== -1) {
          tokens.push({ text: remaining.slice(0, end + 1), type: 'comment' });
          pos += end + 1;
          continue;
        }
        tokens.push({ text: remaining, type: 'comment' });
        break;
      }

      if (remaining.startsWith('{')) {
        const end = remaining.indexOf('}');
        if (end !== -1) {
          tokens.push({ text: remaining.slice(0, end + 1), type: 'string' });
          pos += end + 1;
          continue;
        }
      }
      
      const keywordMatch = remaining.match(/^(global|local|tell|ask|is|now|true|false)\b/);
      if (keywordMatch) {
        tokens.push({ text: keywordMatch[0], type: 'keyword' });
        pos += keywordMatch[0].length;
        continue;
      }
      
      const numberMatch = remaining.match(/^[+-]?(\d+\.\d+|\.\d+|\d+)/);
      if (numberMatch) {
        tokens.push({ text: numberMatch[0], type: 'number' });
        pos += numberMatch[0].length;
        continue;
      }
      
      if (/^[+\-*/%^()]/.test(remaining)) {
        tokens.push({ text: remaining[0], type: 'operator' });
        pos += 1;
        continue;
      }
      
      tokens.push({ text: remaining[0], type: 'text' });
      pos += 1;
    }
    
    if (li < lines.length - 1) {
      tokens.push({ text: '\n', type: 'text' });
    }
  }

  return tokens;
}

function CodeBlock({ code }: { code: string }) {
  const tokens = tokenizeZeta(code);
  
  return (
    <div className="cheatsheet-code">
      {tokens.map((token, i) => (
        token.type === 'text' ? (
          <span key={i}>{token.text}</span>
        ) : (
          <span key={i} className={`hl-${token.type}`}>{token.text}</span>
        )
      ))}
    </div>
  );
}

export default function AboutPanel({ isOpen, onClose }: AboutPanelProps) {
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  useEffect(() => {
    if (!isOpen || !panelRef.current) return;

    const panel = panelRef.current;
    const focusable = panel.querySelectorAll<HTMLElement>(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    const handleTab = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last?.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first?.focus();
      }
    };

    document.addEventListener('keydown', handleTab);
    first?.focus();
    return () => document.removeEventListener('keydown', handleTab);
  }, [isOpen]);

  const cheatsheetData = useMemo(() => CHEATSHEET, []);

  if (!isOpen) return null;

  return (
    <>
      <div className="about-backdrop" onClick={onClose} />
      <div ref={panelRef} className="about-panel" role="dialog" aria-label="About Zeta">
        <div className="about-header">
          <span className="about-title">── ABOUT ──</span>
          <button className="about-close" onClick={onClose} aria-label="Close">[close]</button>
        </div>

        <div className="about-content">
          <section className="about-section">
            <p className="about-text">
              Zeta is a simple, readable programming language with English-like syntax.
              It is designed to be intuitive for beginners while offering enough expressiveness
              for educational programming tasks. The language features a clean, keyword-driven
              syntax that reads like natural language, making it easy to understand and write.
            </p>
          </section>

          <hr className="about-divider" />

          <section className="about-section">
            <span className="about-section-title">── SYNTAX CHEATSHEET ──</span>
            <div className="cheatsheet-grid">
              {cheatsheetData.map((item) => (
                <div key={item.label} className="cheatsheet-item">
                  <span className="cheatsheet-label">{item.label}</span>
                  <CodeBlock code={item.code} />
                </div>
              ))}
            </div>
          </section>

          <hr className="about-divider" />

          <section className="about-section">
            <span className="about-section-title">── HOW THIS RUNS ──</span>
            <p className="about-text">
              The compiler is a Java JAR (the same one in the{' '}
              <a href="https://github.com/AliHamzaAzam/Zeta" target="_blank" rel="noopener noreferrer">
                Zeta repo
              </a>
              ) running entirely in your browser via{' '}
              <a href="https://labs.leaningtech.com/cheerpj" target="_blank" rel="noopener noreferrer">
                CheerpJ
              </a>
              , a WebAssembly JVM. No backend, no server — your code never leaves your machine.
            </p>
          </section>

          <hr className="about-divider" />

          <section className="about-section">
            <span className="about-section-title">── LINKS ──</span>
            <ul className="about-links">
              <li>
                <a href="https://github.com/AliHamzaAzam/Zeta" target="_blank" rel="noopener noreferrer">
                  ↗ compiler source
                </a>
              </li>
              <li>
                <a href="https://github.com/AliHamzaAzam/zeta-playground" target="_blank" rel="noopener noreferrer">
                  ↗ playground source
                </a>
              </li>
              <li>
                <a href="https://alihamzaazam.com" target="_blank" rel="noopener noreferrer">
                  ↗ alihamzaazam.com
                </a>
              </li>
            </ul>
          </section>
        </div>
      </div>
    </>
  );
}
